import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  MeetingRoomBookingViewPanel,
  selectHospitalityMenuItems,
  selectOrderedHospitalityMenuItems,
  selectRootOfficeForMeetingRoom,
  setDialogPanel,
  setOrderedHospitalityMenuItems
} from "../../models/meeting-room-booking-dialog.duck";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { HospitalityOrderItemWithQuantity } from "../../models/hospitality.models";
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { ImportantHospitalityNote } from "./ImportantHospitalityNote";

export function AddItemsFromMenuPanel(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const hospitalityMenuItems = useSelector(selectHospitalityMenuItems);
  const orderedHospitalityMenuItems = useSelector(selectOrderedHospitalityMenuItems);
  const rootOffice = useSelector(selectRootOfficeForMeetingRoom);
  const [menuItems, setMenuItems] = useState<HospitalityOrderItemWithQuantity[]>([]);
  const [hasLoadedMenuItems, setHasLoadedMenuItems] = useState(false);

  useEffect(() => {
    if (!hasLoadedMenuItems) {
      if (hospitalityMenuItems.length > 0 && menuItems.length === 0) {
        let retVal: HospitalityOrderItemWithQuantity[] = [];

        for (const menuItem of hospitalityMenuItems) {
          const orderedItem = orderedHospitalityMenuItems.find(omi => omi.menuItemId === menuItem.id);
          retVal.push({
            ...menuItem,
            id: !!orderedItem ? orderedItem.id : undefined,
            quantity: !!orderedItem ? orderedItem.quantity : 0,
            menuItemId: menuItem.id,
          })
        }
        setMenuItems(retVal.filter(item => item.enabled))
        setHasLoadedMenuItems(true)
      }
    }
  }, [hasLoadedMenuItems, hospitalityMenuItems, menuItems, orderedHospitalityMenuItems]);

  useEffect(() => {
    return () => {
      setMenuItems([]);
      setHasLoadedMenuItems(false);
    }
  }, []);

  const onCancelClicked = () => {
    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));
  }

  const onConfirmClicked = () => {
    dispatch(setDialogPanel(MeetingRoomBookingViewPanel.MeetingRoomBooking));
    dispatch(setOrderedHospitalityMenuItems(menuItems));
  }

  const updateItemQuantity = (item: HospitalityOrderItemWithQuantity, extra: number) => {
    const idx = menuItems.findIndex(i => i.menuItemId === item.menuItemId);
    if (idx > -1) {
      let itemToUpdate = {...menuItems[idx]};
      itemToUpdate.quantity = Math.max(itemToUpdate.quantity + extra, 0);
      let updatedArray = [...menuItems];
      updatedArray.splice(idx, 1, itemToUpdate);
      setMenuItems(updatedArray);
    }
  }

  return (
    <>
      <Content>
        <HeadlineMedium style={{marginBottom: 12}}>{t('hospitality.choose-items')}</HeadlineMedium>
        {rootOffice?.hospitalImportantNoteVisible && <ImportantHospitalityNote office={rootOffice} />}
        <Header>
          <BodySmall weight={600} style={{width: '60%'}}>Item</BodySmall>
          <BodySmall weight={600} style={{width: '15%'}}>Price</BodySmall>
          <BodySmall weight={600} style={{width: '25%'}}>Quantity</BodySmall>
        </Header>
        {menuItems.map((item: HospitalityOrderItemWithQuantity, key: number) => <Row key={key}>
          <BodySmall className={'item__description'}>{item.label}</BodySmall>
          <BodySmall className={'item__price'}>{item.currency}{item.price.toFixed(2)}</BodySmall>

          <Quantity className={'item__quantity'}>
            <IconButton color={'grey'} icon={IconTypes.Minus} onClick={() => updateItemQuantity(item, -1)} />
            <BodySmall className={"quantity__text"}>{item.quantity}</BodySmall>
            <IconButton icon={IconTypes.Add} onClick={() => updateItemQuantity(item, 1)} />
          </Quantity>
        </Row>)}
        <Controls>
          <OutlineButton text={'button.cancel'} click={onCancelClicked} />
          <PrimaryButton text={'hospitality.confirm'} click={onConfirmClicked} />
        </Controls>
      </Content>
    </>
  )
}

interface Props {
}


const Content = styled.div`
  padding: 24px 56px 24px 56px;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${Colours.lightGrey};
  .item__description {
    width: 60%;
  }
  .item__quantity {
    width: 25%;
  }
  .item__price {
    width: 15%;
  }
`

const Quantity = styled.div`
  display: flex;
  align-items: center;
  .quantity__text {
    margin: 0 4px;
  }
  .iconButton__button {
    height: 32px;
    width: 32px;
    border: 1px solid ${Colours.darkGrey};
    border-radius: 100%;
    margin: 0 8px;
    i {
      font-size: 14px;
    }
  }
`
